import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useGlobal } from '@/context/GlobalContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { Save, RefreshCw, ChevronDown, ChevronRight, Image, Type, List, FileText } from 'lucide-react';

const AdminPageContent = () => {
  const { token } = useAuth();
  const { API } = useGlobal();
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [editedContent, setEditedContent] = useState({});

  const pages = [
    { id: 'home', label: 'Homepage' },
    { id: 'mobility', label: 'Mobility Imaging' },
    { id: 'surveillance', label: 'Surveillance' },
    { id: 'ultrasonic', label: 'Ultrasonic' },
    { id: 'engineering', label: 'Engineering/OEM' },
    { id: 'about', label: 'About Us' },
    { id: 'contact', label: 'Contact' },
  ];

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/page-content`);
      setContent(response.data);
      
      // Initialize edited content
      const edited = {};
      response.data.forEach(item => {
        const key = `${item.page}_${item.section}`;
        edited[key] = item.content;
      });
      setEditedContent(edited);
    } catch (error) {
      console.error('Error fetching content:', error);
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const initializeContent = async () => {
    try {
      setLoading(true);
      await axios.post(`${API}/page-content/init`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Content initialized successfully');
      await fetchContent();
    } catch (error) {
      toast.error('Failed to initialize content');
    } finally {
      setLoading(false);
    }
  };

  const saveSection = async (page, section) => {
    const key = `${page}_${section}`;
    setSaving(true);
    try {
      await axios.put(`${API}/page-content/${page}/${section}`, 
        { content: editedContent[key] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${section} saved successfully`);
    } catch (error) {
      toast.error('Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const updateField = (page, section, path, value) => {
    const key = `${page}_${section}`;
    setEditedContent(prev => {
      const newContent = { ...prev };
      const sectionContent = { ...newContent[key] };
      
      // Handle nested paths like "pillar1.title"
      const pathParts = path.split('.');
      let current = sectionContent;
      for (let i = 0; i < pathParts.length - 1; i++) {
        if (typeof current[pathParts[i]] === 'object') {
          current[pathParts[i]] = { ...current[pathParts[i]] };
          current = current[pathParts[i]];
        }
      }
      current[pathParts[pathParts.length - 1]] = value;
      
      newContent[key] = sectionContent;
      return newContent;
    });
  };

  const renderField = (page, section, key, value, path = key) => {
    const fullKey = `${page}_${section}`;
    
    if (typeof value === 'string') {
      const isImage = key.toLowerCase().includes('image') || key.toLowerCase().includes('url');
      const isLongText = value.length > 100 || key.toLowerCase().includes('description') || key.toLowerCase().includes('paragraph');
      
      return (
        <div key={path} className="mb-4">
          <label className="block text-sm font-medium text-slate-300 mb-1 capitalize">
            {key.replace(/_/g, ' ')}
            {isImage && <Image className="inline w-4 h-4 ml-1 text-slate-500" />}
          </label>
          {isLongText ? (
            <Textarea
              value={editedContent[fullKey]?.[path] ?? value}
              onChange={(e) => updateField(page, section, path, e.target.value)}
              className="bg-[#1a1f2e] border-[#3d4758] text-white"
              rows={3}
            />
          ) : (
            <Input
              value={editedContent[fullKey]?.[path] ?? value}
              onChange={(e) => updateField(page, section, path, e.target.value)}
              className="bg-[#1a1f2e] border-[#3d4758] text-white"
            />
          )}
          {isImage && value && (
            <img src={value} alt="preview" className="mt-2 h-20 rounded object-cover" />
          )}
        </div>
      );
    }
    
    if (Array.isArray(value)) {
      return (
        <div key={path} className="mb-4">
          <label className="block text-sm font-medium text-slate-300 mb-2 capitalize">
            <List className="inline w-4 h-4 mr-1" />
            {key.replace(/_/g, ' ')} ({value.length} items)
          </label>
          <div className="pl-4 border-l-2 border-[#3d4758] space-y-2">
            {value.map((item, idx) => {
              if (typeof item === 'string') {
                return (
                  <Input
                    key={idx}
                    value={item}
                    onChange={(e) => {
                      const newArray = [...value];
                      newArray[idx] = e.target.value;
                      updateField(page, section, path, newArray);
                    }}
                    className="bg-[#1a1f2e] border-[#3d4758] text-white"
                  />
                );
              } else if (typeof item === 'object') {
                return (
                  <div key={idx} className="bg-[#1a1f2e] p-3 rounded border border-[#3d4758]">
                    <div className="text-xs text-slate-500 mb-2">Item {idx + 1}</div>
                    {Object.entries(item).map(([itemKey, itemValue]) => (
                      <div key={itemKey} className="mb-2">
                        <label className="block text-xs text-slate-400 mb-1 capitalize">{itemKey}</label>
                        {typeof itemValue === 'string' ? (
                          <Input
                            value={itemValue}
                            onChange={(e) => {
                              const newArray = [...value];
                              newArray[idx] = { ...newArray[idx], [itemKey]: e.target.value };
                              updateField(page, section, path, newArray);
                            }}
                            className="bg-[#232a3b] border-[#3d4758] text-white text-sm"
                          />
                        ) : Array.isArray(itemValue) ? (
                          <Input
                            value={itemValue.join(', ')}
                            onChange={(e) => {
                              const newArray = [...value];
                              newArray[idx] = { ...newArray[idx], [itemKey]: e.target.value.split(', ') };
                              updateField(page, section, path, newArray);
                            }}
                            className="bg-[#232a3b] border-[#3d4758] text-white text-sm"
                            placeholder="Comma separated values"
                          />
                        ) : null}
                      </div>
                    ))}
                  </div>
                );
              }
              return null;
            })}
          </div>
        </div>
      );
    }
    
    if (typeof value === 'object' && value !== null) {
      return (
        <div key={path} className="mb-4">
          <label className="block text-sm font-medium text-slate-300 mb-2 capitalize">
            <FileText className="inline w-4 h-4 mr-1" />
            {key.replace(/_/g, ' ')}
          </label>
          <div className="pl-4 border-l-2 border-[#5b3589] space-y-2">
            {Object.entries(value).map(([nestedKey, nestedValue]) => 
              renderField(page, section, nestedKey, nestedValue, `${path}.${nestedKey}`)
            )}
          </div>
        </div>
      );
    }
    
    return null;
  };

  const getPageContent = (pageId) => {
    return content.filter(item => item.page === pageId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[#5b3589]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-page-content">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Page Content</h1>
          <p className="text-slate-400">Edit text and images across your website</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={initializeContent} 
            variant="outline" 
            className="border-slate-600 text-slate-300"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Initialize Defaults
          </Button>
        </div>
      </div>

      {content.length === 0 ? (
        <div className="bg-[#232a3b] rounded-xl p-8 text-center">
          <p className="text-slate-400 mb-4">No content found. Click "Initialize Defaults" to set up editable content.</p>
          <Button onClick={initializeContent} className="bg-[#5b3589] hover:bg-[#6d3d9e]">
            Initialize Content
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {pages.map(page => {
            const pageContent = getPageContent(page.id);
            if (pageContent.length === 0) return null;
            
            const pageKey = `page_${page.id}`;
            const isPageExpanded = expandedSections[pageKey] !== false;
            
            return (
              <div key={page.id} className="bg-[#232a3b] rounded-xl border border-[#3d4758] overflow-hidden">
                <button
                  onClick={() => toggleSection(pageKey)}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-[#2a324a]"
                >
                  <div className="flex items-center gap-3">
                    {isPageExpanded ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
                    <span className="text-lg font-semibold text-white">{page.label}</span>
                    <span className="text-sm text-slate-500">({pageContent.length} sections)</span>
                  </div>
                </button>
                
                {isPageExpanded && (
                  <div className="border-t border-[#3d4758]">
                    {pageContent.map(item => {
                      const sectionKey = `${item.page}_${item.section}`;
                      const isSectionExpanded = expandedSections[sectionKey];
                      
                      return (
                        <div key={sectionKey} className="border-b border-[#3d4758] last:border-b-0">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleSection(sectionKey);
                            }}
                            className="w-full flex items-center justify-between p-3 pl-8 text-left hover:bg-[#2a324a]"
                            data-testid={`section-toggle-${item.section}`}
                          >
                            <div className="flex items-center gap-2">
                              {isSectionExpanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                              <span className="text-slate-300 capitalize">Section: {item.section.replace(/_/g, ' ')}</span>
                            </div>
                          </button>
                          
                          {isSectionExpanded && (
                            <div className="p-4 pl-12 bg-[#1a1f2e]">
                              {Object.entries(editedContent[sectionKey] || item.content).map(([key, value]) => 
                                renderField(item.page, item.section, key, value)
                              )}
                              <div className="flex justify-end mt-4 pt-4 border-t border-[#3d4758]">
                                <Button 
                                  onClick={() => saveSection(item.page, item.section)}
                                  disabled={saving}
                                  className="bg-[#5b3589] hover:bg-[#6d3d9e]"
                                >
                                  <Save className="w-4 h-4 mr-2" />
                                  {saving ? 'Saving...' : 'Save Section'}
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminPageContent;
